import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { PenLine, Link, FileUp, Zap, Upload } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { JOB_STATUSES, JOB_STAGES, WORK_SETUPS, JOB_SOURCES } from '../utils/constants';
import './AddJobModal.css';

const EMPTY_FORM = {
  company: '', role: '', source: '', postingLink: '',
  applicationDate: new Date().toISOString().split('T')[0],
  location: '', workSetup: 'Not specified',
  salaryMin: '', salaryMax: '', salaryCurrency: 'PHP',
  status: 'Applied', stage: '', nextStep: '', benefits: '', notes: ''
};

const AddJobModal = ({ onClose, onAdd, onUpgrade }) => {
  const { refreshUser } = useAuth();
  const [tab, setTab] = useState('manual'); // 'manual' | 'url' | 'pdf'
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [urlInput, setUrlInput] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState({});
  const [salaryMode, setSalaryMode] = useState('range'); // 'range' | 'exact'
  const fileRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleParseUrl = async () => {
    if (!urlInput.trim()) return toast.error('Please enter a URL');
    setParsing(true);
    try {
      const res = await api.post('/api/parse/url', { url: urlInput.trim() });
      const data = res.data.parsed;
      setForm(f => ({
        ...f,
        company: data.company || f.company,
        role: data.role || f.role,
        source: data.source || f.source,
        postingLink: data.postingLink || urlInput.trim(),
        location: data.location || f.location,
        workSetup: data.workSetup || f.workSetup,
        salaryMin: data.salaryMin || f.salaryMin,
        salaryMax: data.salaryMax || f.salaryMax,
        salaryCurrency: data.salaryCurrency || 'PHP',
        benefits: data.benefits || f.benefits,
        notes: data.notes || f.notes,
      }));
      setParsed(true);
      toast.success('Job details extracted! Review and submit.');
      setTab('manual');
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to parse URL');
    } finally {
      setParsing(false);
    }
  };

  const handleParsePdf = async (file) => {
    if (!file || file.type !== 'application/pdf') return toast.error('Please provide a PDF file');
    setParsing(true);
    const formData = new FormData();
    formData.append('pdf', file);
    try {
      const res = await api.post('/api/parse/pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const data = res.data.parsed;
      setForm(f => ({
        ...f,
        company: data.company || f.company,
        role: data.role || f.role,
        source: data.source || f.source,
        location: data.location || f.location,
        workSetup: data.workSetup || f.workSetup,
        salaryMin: data.salaryMin || f.salaryMin,
        salaryMax: data.salaryMax || f.salaryMax,
        salaryCurrency: data.salaryCurrency || 'PHP',
        benefits: data.benefits || f.benefits,
        notes: data.notes || f.notes,
      }));
      setParsed(true);
      toast.success('PDF parsed! Review and submit.');
      setTab('manual');
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to parse PDF');
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleParsePdf(file);
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!form.company.trim()) newErrors.company = true;
    if (!form.role.trim()) newErrors.role = true;
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setErrors({});
    setSaving(true);
    try {
      await onAdd({
        ...form,
        salaryMin: form.salaryMin ? Number(form.salaryMin) : null,
        salaryMax: form.salaryMax ? Number(form.salaryMax) : null,
      });
    } catch (e) {
      // errors handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="add-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="add-modal-panel">
        {/* Header */}
        <div className="add-modal-header">
          <div>
            <h2 className="add-modal-title">Add Application</h2>
            <p className="add-modal-subtitle">Track a new job application</p>
          </div>
          <button className="add-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div className="add-modal-tabs">
          <button
            className={`add-tab ${tab === 'manual' ? 'active' : ''}`}
            onClick={() => setTab('manual')}
          >
            <PenLine size={13} /> Manual Entry
          </button>
          <button
            className={`add-tab ${tab === 'url' ? 'active' : ''}`}
            onClick={() => setTab('url')}
          >
            <Link size={13} /> Paste Link <span className="add-tab-cost">1 cr</span>
          </button>
          <button
            className={`add-tab ${tab === 'pdf' ? 'active' : ''}`}
            onClick={() => setTab('pdf')}
          >
            <FileUp size={13} /> Upload PDF <span className="add-tab-cost">1 cr</span>
          </button>
        </div>

        <div className="add-modal-body">
          {/* URL Parser */}
          {tab === 'url' && (
            <div className="parse-section">
              <p className="parse-hint">
                Paste a job posting link and AI will extract the details automatically.
                <br />
                <span className="parse-hint-note">⚠️ Some sites may block automated access — use PDF upload instead.</span>
              </p>
              <div className="url-input-row">
                <input
                  className="url-input"
                  type="url"
                  placeholder="https://www.jobstreet.com.ph/job/..."
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleParseUrl()}
                />
                <button
                  className="parse-btn"
                  onClick={handleParseUrl}
                  disabled={parsing}
                >
                  {parsing ? <span className="btn-spinner" /> : <><Zap size={13} /> Extract</>}
                </button>
              </div>
              {parsing && (
                <div className="parse-loading">
                  <div className="loading-spinner" />
                  <span>AI is reading the job posting...</span>
                </div>
              )}
            </div>
          )}

          {/* PDF Parser */}
          {tab === 'pdf' && (
            <div className="parse-section">
              <p className="parse-hint">
                Download the job posting as PDF, then upload it here for AI parsing.
              </p>
              <div
                className={`pdf-drop-zone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
              >
                {parsing ? (
                  <div className="pdf-drop-content">
                    <div className="loading-spinner" />
                    <span>Parsing PDF with AI...</span>
                  </div>
                ) : (
                  <div className="pdf-drop-content">
                    <span className="pdf-drop-icon"><Upload size={28} strokeWidth={1.5} /></span>
                    <span className="pdf-drop-label">Drop PDF here or click to browse</span>
                    <span className="pdf-drop-sub">Max 10MB · Text-based PDFs only</span>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  onChange={e => e.target.files[0] && handleParsePdf(e.target.files[0])}
                />
              </div>
            </div>
          )}

          {/* Form — always visible in manual tab, also shown after parsing */}
          {tab === 'manual' && (
            <div className="add-form">
              {parsed && (
                <div className="parsed-notice">
                  Fields filled from AI parsing — please review and adjust as needed.
                </div>
              )}

              <div className="form-grid-2">
                <div className="form-field required">
                  <label>Company *</label>
                  <input className={`form-input${errors.company ? ' form-input-error' : ''}`} value={form.company}
                    onChange={e => { set('company', e.target.value); if (errors.company) setErrors(p => ({ ...p, company: false })); }}
                    placeholder="e.g. Company X" />
                  {errors.company && <span className="form-field-error-msg">Company name is required</span>}
                </div>
                <div className="form-field required">
                  <label>Role / Position *</label>
                  <input className={`form-input${errors.role ? ' form-input-error' : ''}`} value={form.role}
                    onChange={e => { set('role', e.target.value); if (errors.role) setErrors(p => ({ ...p, role: false })); }}
                    placeholder="e.g. Software Engineer" />
                  {errors.role && <span className="form-field-error-msg">Role is required</span>}
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Source</label>
                  <select className="form-select" value={form.source} onChange={e => set('source', e.target.value)}>
                    <option value="">Select source...</option>
                    {JOB_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Application Date</label>
                  <input className="form-input" type="date" value={form.applicationDate} onChange={e => set('applicationDate', e.target.value)} />
                </div>
              </div>

              <div className="form-field">
                <label>Job Posting Link</label>
                <input className="form-input" type="url" value={form.postingLink} onChange={e => set('postingLink', e.target.value)} placeholder="https://..." />
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Location</label>
                  <input className="form-input" value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Taguig, Metro Manila" />
                </div>
                <div className="form-field">
                  <label>Work Setup</label>
                  <select className="form-select" value={form.workSetup} onChange={e => set('workSetup', e.target.value)}>
                    {WORK_SETUPS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="salary-mode-row">
                <span className="salary-mode-label">Salary</span>
                <div className="salary-mode-toggle">
                  <button type="button" className={`stoggle-btn ${salaryMode === 'range' ? 'active' : ''}`}
                    onClick={() => setSalaryMode('range')}>Min / Max</button>
                  <button type="button" className={`stoggle-btn ${salaryMode === 'exact' ? 'active' : ''}`}
                    onClick={() => { setSalaryMode('exact'); set('salaryMax', ''); }}>Exact</button>
                </div>
              </div>

              {salaryMode === 'range' ? (
                <div className="form-grid-3">
                  <div className="form-field">
                    <label>Salary Min</label>
                    <input className="form-input" type="number" value={form.salaryMin} onChange={e => set('salaryMin', e.target.value)} placeholder="40000" />
                  </div>
                  <div className="form-field">
                    <label>Salary Max</label>
                    <input className="form-input" type="number" value={form.salaryMax} onChange={e => set('salaryMax', e.target.value)} placeholder="60000" />
                  </div>
                  <div className="form-field">
                    <label>Currency</label>
                    <input className="form-input" value={form.salaryCurrency} onChange={e => set('salaryCurrency', e.target.value)} placeholder="PHP" />
                  </div>
                </div>
              ) : (
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Salary (Exact)</label>
                    <input className="form-input" type="number" value={form.salaryMin}
                      onChange={e => { set('salaryMin', e.target.value); set('salaryMax', e.target.value); }}
                      placeholder="e.g. 50000" />
                  </div>
                  <div className="form-field">
                    <label>Currency</label>
                    <input className="form-input" value={form.salaryCurrency} onChange={e => set('salaryCurrency', e.target.value)} placeholder="PHP" />
                  </div>
                </div>
              )}

              <div className="form-section-divider">Application Status</div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Status</label>
                  <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                    {JOB_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Stage</label>
                  <select className="form-select" value={form.stage} onChange={e => set('stage', e.target.value)}>
                    <option value="">—</option>
                    {JOB_STAGES.filter(s => s).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-field">
                <label>Next Step / Action</label>
                <input className="form-input" value={form.nextStep} onChange={e => set('nextStep', e.target.value)} placeholder="e.g. Wait for HR callback by Friday" />
              </div>

              <div className="form-section-divider">Additional Details</div>

              <div className="form-field">
                <label>Benefits</label>
                <textarea className="form-textarea" value={form.benefits} onChange={e => set('benefits', e.target.value)} rows={2} placeholder="e.g. HMO, 13th/14th month, meal allowance, WFH setup" />
              </div>

              <div className="form-field">
                <label>Job Requirements / Qualifications</label>
                <textarea className="form-textarea" value={form.requirements} onChange={e => set('requirements', e.target.value)} rows={2} placeholder="e.g. 3+ yrs React, Bachelor's in CS, strong SQL skills..." />
              </div>

              <div className="form-field">
                <label>Notes</label>
                <textarea className="form-textarea" value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="Any other personal notes about this opportunity..." />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="add-modal-footer">
          <button className="footer-btn-cancel" onClick={onClose}>Cancel</button>
          {tab === 'manual' && (
            <button className="footer-btn-save" onClick={handleSubmit} disabled={saving}>
              {saving ? <><span className="btn-spinner" /> Saving...</> : '+ Add Application'}
            </button>
          )}
          {tab === 'url' && (
            <button className="footer-btn-alt" onClick={() => setTab('manual')}>
              Switch to Manual Entry →
            </button>
          )}
          {tab === 'pdf' && (
            <button className="footer-btn-alt" onClick={() => setTab('manual')}>
              Switch to Manual Entry →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddJobModal;
